import { useEffect, useState } from "react";
import { Client } from "../../../models/ClientModels";
import "./ClientModal.css";

export type ClientModalMode = "create" | "edit";

interface ClientModalProps {
  isOpen: boolean;
  mode: ClientModalMode;
  client: Client | null;
  submitting: boolean;
  initial?: Partial<ClientFormValues>;
  onClose: () => void;
  onSubmit: (form: ClientFormValues) => Promise<void>;
}

export interface ClientFormValues {
  fullName: string;
  phone: string;
  email: string;
  gender: string;
  age: string;
  address: string;
  city: string;
  area: string;
  notes: string;
  isActive: boolean;
}

const emptyForm: ClientFormValues = {
  fullName: "",
  phone: "",
  email: "",
  gender: "",
  age: "",
  address: "",
  city: "",
  area: "",
  notes: "",
  isActive: true,
};

const fromClient = (client: Client): ClientFormValues => ({
  fullName: client.fullName,
  phone: client.phone,
  email: client.email || "",
  gender: client.gender || "",
  age:
    client.age !== null && client.age !== undefined
      ? String(client.age)
      : "",
  address: client.address || "",
  city: client.city || "",
  area: client.area || "",
  notes: client.notes || "",
  isActive: client.isActive,
});

type TextKey = Exclude<keyof ClientFormValues, "isActive" | "gender">;

const ClientModal = ({
  isOpen,
  mode,
  client,
  submitting,
  initial,
  onClose,
  onSubmit,
}: ClientModalProps) => {
  const [form, setForm] = useState<ClientFormValues>(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      setForm({ ...emptyForm, ...initial });
    } else if (client) {
      setForm(fromClient(client));
    }
  }, [isOpen, mode, client, initial]);

  if (!isOpen) return null;

  const setField = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const textField = (
    key: TextKey,
    label: string,
    opts?: { type?: string; placeholder?: string; full?: boolean },
  ) => (
    <div
      className={`client-form-group ${opts?.full ? "client-form-group--full" : ""}`}
    >
      <label htmlFor={`client-${key}`}>{label}</label>
      <input
        id={`client-${key}`}
        type={opts?.type ?? "text"}
        value={form[key]}
        placeholder={opts?.placeholder}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  return (
    <div className="client-modal-overlay">
      <div className="client-modal" onClick={(e) => e.stopPropagation()}>
        <div className="client-modal__header">
          <h3>{mode === "create" ? "Add Client" : "Edit Client"}</h3>
          <button
            className="client-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="client-modal__body" onSubmit={handleSubmit}>
          <div className="client-form-grid">
            <div className="client-form-group client-form-group--full">
              <label htmlFor="client-fullName">Full Name *</label>
              <input
                id="client-fullName"
                type="text"
                value={form.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
              />
            </div>

            {textField("phone", "Phone *", { type: "tel" })}
            {textField("email", "Email", { type: "email" })}

            <div className="client-form-group">
              <label htmlFor="client-gender">Gender</label>
              <select
                id="client-gender"
                value={form.gender}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, gender: e.target.value }))
                }
              >
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="client-form-group">
              <label htmlFor="client-age">Age</label>
              <input
                id="client-age"
                type="number"
                min={0}
                max={150}
                value={form.age}
                onChange={(e) => setField("age", e.target.value)}
              />
            </div>

            {textField("city", "City")}
            {textField("area", "Area")}
            {textField("address", "Address", { full: true })}
          </div>

          <div className="client-form-group client-form-group--full">
            <label htmlFor="client-notes">Notes</label>
            <textarea
              id="client-notes"
              rows={3}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
            />
          </div>

          <div className="client-form-group client-form-group--full">
            <label className="client-form-check">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              Active
            </label>
          </div>

          <div className="client-modal__actions">
            <button
              type="button"
              className="client-btn client-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="client-btn client-btn--primary"
              disabled={submitting || !form.fullName.trim() || !form.phone.trim()}
            >
              {submitting
                ? "Saving..."
                : mode === "create"
                  ? "Add Client"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;